import { createContext, useState } from "react";
import { apiV1 } from "../lib/axios";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

export const PayrollContext = createContext({});

export function PayrollProvider({ children }) {
  const [payrolls, setPayrolls] = useState();
  const [selectedPayroll, setSelectedPayroll] = useState();
  const [dashboardInfos, setDashboardInfos] = useState();
  const [detailedPayroll, setDetailedPayroll] = useState();
  const [loading, setLoading] = useState(false);

  async function listPayrolls() {
    try{
      setLoading(true);
      const response = await apiV1.get("/payroll/list");
      
      setPayrolls(response.data.data);
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function getDashboardInfos(referenceMonth, referenceYear) {
    try{
      setLoading(true);
      const response = await apiV1.get(`/payroll/dashboard?referenceMonth=${referenceMonth}&referenceYear=${referenceYear}`);
      
      setDashboardInfos(response.data.data);
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function createPayroll(data) {
    try{
      setLoading(true);
      await apiV1.post("/payroll/create", data);
      
      await listPayrolls();
      toast.success("folha de pagamento criada com sucesso.");
    } catch (err) {
      console.log("err ", err);
      toast.error(getApiErrorMessage(err, 'Erro ao criar folha de pagamento.'));
    } finally {
      setLoading(false);
    }
  }

  async function getDetailedPayroll(payrollId) {
    try{
      setLoading(true);
      const response = await apiV1.get(`/payroll/details/${payrollId}`);
      
      setDetailedPayroll(response.data.data);
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function generatePayroll(payrollId, collaboratorsToExclude) {
    try{
      setLoading(true);
      await apiV1.post(`/payroll/generate`, { payrollId, collaboratorsToExclude });
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function lockPayroll(payrollId) {
    try{
      setLoading(true);
      await apiV1.post(`/payroll/lock`, { payrollId });
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function getHolerite(payrollItemSnapshotId) {
    try{
      setLoading(true);
      const response = await apiV1.post(`/payroll/holerite`, { payrollItemSnapshotId}, { responseType: 'blob' });
      downloadFile(response.data, 'holerite.pdf');
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  function downloadFile(blobData, filename) {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);

    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  }

  async function sendHoletireByEmail(payrollItemSnapshotId) {
    try{
      setLoading(true);
      await apiV1.post(`/payroll/holerite/email`, { payrollItemSnapshotId });
      toast.success("Holerite enviado por email com sucesso!");
    } catch (err) {
      console.log("err ", err);
      toast.error(getApiErrorMessage(err, 'Erro ao enviar holerite por email.'));
    } finally {
      setLoading(false);
    }
  }

  async function schedulePayrollPayment(payrollId, scheduledAt, collaboratorsToExclude) {
    try{
      setLoading(true);
      const response = await apiV1.post(`/payroll/pay`, { payrollId, scheduledAt, collaboratorsToExclude });
      return response.data;
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function createCollaborator(data) {
    try{
      setLoading(true);
      await apiV1.post("collaborator/create", data);
      toast.success("Colaborador cadastrado com sucesso!");
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function updateCollaborator(data) {
    try{
      setLoading(true);
      await apiV1.post("collaborator/update", data);
      toast.success("Colaborador atualizado com sucesso!");
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function inactiveCollaborator(collaboratorId, terminationDate) {
    try{
      setLoading(true);
      await apiV1.post('collaborator/inactivate', { collaboratorId, terminationDate });
      toast.success("Colaborador deletado com sucesso!");
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function listCollaborators(page = 1, limit = 10) {
    try{
      setLoading(true);
      const response = await apiV1.get("collaborator/list", { params: { page, limit } });
      const payload = response?.data || {};

      const collaborators = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.data?.data)
          ? payload.data.data
          : Array.isArray(payload)
            ? payload
            : [];

      const pagination = payload?.pagination || payload?.data?.pagination || {};

      return {
        data: collaborators,
        pagination,
      };
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function getCollaborator(collaboratorId) {
    try{
      setLoading(true);
      const response = await apiV1.get(`collaborator/details/${collaboratorId}`);
      return response.data;
    } catch (err) {
      console.log("err ", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  return (
    <PayrollContext.Provider
      value={{
        payrolls,
        setPayrolls,
        selectedPayroll,
        setSelectedPayroll,
        payrollLoading: loading,
        loading,
        setPayrollLoading: setLoading,
        dashboardInfos,
        setDashboardInfos,
        detailedPayroll,
        setDetailedPayroll,
        updateCollaborator,
        getCollaborator,
        listPayrolls,
        getDashboardInfos,
        createPayroll,
        getDetailedPayroll,
        generatePayroll,
        lockPayroll,
        getHolerite,
        sendHoletireByEmail,
        schedulePayrollPayment,
        createCollaborator,
        listCollaborators,
        inactiveCollaborator
      }}
    >
      {children}
    </PayrollContext.Provider>
  );
}
